import { throttle, delay, extend, isObject, isFunction, get } from 'lodash-es';
import debug from 'debug';
import { watch } from 'vue';
import { ComponentValueCache } from '../caching';
import { markErrorShown } from '../errors';
import { ComponentDataSpec, ErrorHandlingMode } from '../schemas';
import type { UseDataLoaderOptions } from './types';

const log = debug('itp:core:useDataLoader');

const isThenable = (value: any): value is Promise<any> =>
  value != null && typeof value.then === 'function';

export function useDataLoader({
  state,
  friendlyName,
  dataSpecs,
  loaders,
  context,
  ux,
  helpers,
  onReloadStart,
}: UseDataLoaderOptions) {
  const DEFAULT_LOADING_INDICATOR_DELAY_MS = 300;
  const loadingIndicatorDelayMs =
    ux && typeof ux.loadingIndicatorDelay === 'number'
      ? ux.loadingIndicatorDelay
      : DEFAULT_LOADING_INDICATOR_DELAY_MS;
  let pendingIndicatorTimer: ReturnType<typeof setTimeout> | null = null;

  const cancelPendingIndicator = () => {
    if (pendingIndicatorTimer !== null) {
      clearTimeout(pendingIndicatorTimer);
      pendingIndicatorTimer = null;
    }
  };

  const onLoadStart = () => {
    if (state.showLoadingIndicator?.value) {
      return;
    }
    cancelPendingIndicator();
    if (loadingIndicatorDelayMs <= 0) {
      if (state.showLoadingIndicator) {
        state.showLoadingIndicator.value = true;
      }
      return;
    }
    pendingIndicatorTimer = setTimeout(() => {
      pendingIndicatorTimer = null;
      if (state.loading?.value || state.reloading?.value) {
        if (state.showLoadingIndicator) {
          state.showLoadingIndicator.value = true;
        }
      }
    }, loadingIndicatorDelayMs);
  };

  const onLoadEnd = () => {
    cancelPendingIndicator();
    if (state.showLoadingIndicator) {
      state.showLoadingIndicator.value = false;
    }
  };

  const touchLastLoaded = () => {
    state.loading.value = false;
    state.reloading.value = false;
    state.loaded.value = true;
    state.error.value = null;
    state.lastLoaded.value = Math.round(Date.now() / 1000);
    onLoadEnd();
    log('[%s] lastloaded: %s', friendlyName(), state.lastLoaded.value);
  };

  const showError = (err: any, inPlace: boolean) => {
    console.error(err);
    markErrorShown(err);
    if (!inPlace && helpers.$imodal?.value) {
      helpers.$imodal.value.error({ error: err });
    } else {
      state.error.value = err;
    }
  };

  const handleError = (err: any, mode?: ErrorHandlingMode) =>
    showError(err, mode === 'local' || ux?.errorHandling === 'local');

  const handleLoadError = (err: any) => {
    showError(err, ux?.errorHandling !== 'global');
    state.loading.value = false;
    state.reloading.value = false;
    onLoadEnd();
  };

  const _loadFromCache = (name: string, spec: ComponentDataSpec) => {
    if (spec.cache) {
      const cacheInstance = new ComponentValueCache(
        context,
        name,
        spec.cache.key,
      );
      const value = cacheInstance.getValue();

      if (value != null) {
        log('[%s] from cache: %s', friendlyName(), name);
        return value;
      }
    }
    return undefined;
  };

  let cacheWatchersRegistered = false;

  const _registerCacheWatchers = () => {
    if (cacheWatchersRegistered) {
      return;
    }
    cacheWatchersRegistered = true;

    for (const spec of dataSpecs) {
      if (!spec.cache) {
        continue;
      }
      const name = spec.name;
      const dataRef = state[name];
      if (!dataRef) {
        continue;
      }

      const cacheInstance = new ComponentValueCache(
        context,
        name,
        spec.cache.key,
      );

      watch(
        dataRef,
        (newValue: any) => {
          cacheInstance.persist(newValue);
        },
        { deep: true },
      );

      log('[%s] registered cache watcher for: %s', friendlyName(), name);
    }
  };

  const _loadDefaultData = (name: string) => {
    const loaderFn = loaders[name];
    if (isFunction(loaderFn)) {
      return loaderFn();
    }
    return undefined;
  };

  const _loadData = (options: {
    useCache: boolean;
  }): Record<string, any> | Promise<Record<string, any>> => {
    const result: Record<string, any> = {};
    const { useCache } = options;

    const specsWithLoaders = new Set(
      dataSpecs
        .filter((spec) => isFunction(loaders[spec.name]))
        .map((spec) => spec.name),
    );

    const completed = new Set<string>();

    const loadSpec = (spec: ComponentDataSpec): void | Promise<void> => {
      const name = spec.name;
      const currentValue = state[name]?.value;
      const uiSkipComponentReload = get(
        currentValue,
        'uiSkipComponentReload',
        false,
      );

      if (uiSkipComponentReload) {
        completed.add(name);
        return;
      }

      const useCachedValue = (cached: any): void | Promise<void> => {
        const takeCached = (defaultValue: any) => {
          if (isObject(defaultValue) && isObject(cached)) {
            extend({}, defaultValue, cached);
            log('[%s] extend cache with default value: %o', defaultValue);
          }

          if (state[name]) {
            state[name].value = cached;
          }
          result[name] = cached;
          completed.add(name);
        };

        const defaultValue = _loadDefaultData(name);
        return isThenable(defaultValue)
          ? defaultValue.then(takeCached)
          : takeCached(defaultValue);
      };

      const store = (defaultValue: any) => {
        if (defaultValue != null) {
          if (state[name]) {
            state[name].value = defaultValue;
          }
          result[name] = defaultValue;
        }
        completed.add(name);
      };

      const loadDefaultValue = (): void | Promise<void> => {
        const defaultValue = _loadDefaultData(name);
        return isThenable(defaultValue)
          ? defaultValue.then(store)
          : store(defaultValue);
      };

      if (useCache) {
        const cached = _loadFromCache(name, spec);
        if (isThenable(cached)) {
          return cached.then((value: any) =>
            value != null ? useCachedValue(value) : loadDefaultValue(),
          );
        }
        if (cached != null) {
          return useCachedValue(cached);
        }
      }

      return loadDefaultValue();
    };

    const runWaves = (
      specs: ComponentDataSpec[],
    ): Record<string, any> | Promise<Record<string, any>> => {
      let pendingSpecs = specs;

      while (pendingSpecs.length > 0) {
        const ready: ComponentDataSpec[] = [];
        const notReady: ComponentDataSpec[] = [];

        for (const spec of pendingSpecs) {
          const asyncDeps = (spec.dependsOn ?? []).filter((dep) =>
            specsWithLoaders.has(dep),
          );
          const allDepsCompleted = asyncDeps.every((dep) => completed.has(dep));
          if (allDepsCompleted) {
            ready.push(spec);
          } else {
            notReady.push(spec);
          }
        }

        if (ready.length === 0) {
          log(
            '[%s] wave execution stalled: possible circular dependency',
            friendlyName(),
          );
          break;
        }

        const stillLoading = ready.map(loadSpec).filter(isThenable);

        if (stillLoading.length > 0) {
          return Promise.all(stillLoading).then(() => runWaves(notReady));
        }

        pendingSpecs = notReady;
      }

      return result;
    };

    return runWaves([...dataSpecs]);
  };

  const startLoading = (prop?: string | null, opts?: { initial: boolean }) => {
    if (!state.canLoadData.value) {
      log(
        '[%s] loading is already in progress, prevent reload.',
        friendlyName(),
      );
      return Promise.resolve();
    }

    if (!dataSpecs || dataSpecs.length === 0) {
      log('[%s] no data spec, nothing to load.', friendlyName());
      touchLastLoaded();
      return Promise.resolve();
    }

    let delayVal = 0;
    if (opts && opts.initial) {
      state.loading.value = true;
      if (ux && ux.initialLoadDelay) {
        delayVal = ux.initialLoadDelay;
      }
    } else {
      state.reloading.value = true;
    }
    onLoadStart();

    const finish = (res: Record<string, any>) => {
      log('[%s] data loaded: %o', friendlyName(), res);
      if (opts && opts.initial) {
        _registerCacheWatchers();
      }
      touchLastLoaded();
    };

    const run = (): void | Promise<void> => {
      log('[%s] loading data...', friendlyName());
      const outcome = _loadData({ useCache: (opts && opts.initial) || false });
      return isThenable(outcome) ? outcome.then(finish) : finish(outcome);
    };

    if (delayVal > 0) {
      return new Promise<void>((resolve, reject) => {
        const onErr = (e: any) => {
          handleLoadError(e);
          reject(e);
        };
        delay(() => {
          try {
            const running = run();
            if (isThenable(running)) {
              running.then(() => resolve(), onErr);
            } else {
              resolve();
            }
          } catch (e) {
            onErr(e);
          }
        }, delayVal);
      });
    }

    try {
      const running = run();
      if (isThenable(running)) {
        return running.catch((e: any) => {
          handleLoadError(e);
          throw e;
        });
      }
      return Promise.resolve();
    } catch (e) {
      handleLoadError(e);
      return Promise.reject(e);
    }
  };

  const loadData = (prop?: string | null, opts?: { initial: boolean }) => {
    const loading = startLoading(prop, opts);
    const finishReload = opts?.initial ? undefined : onReloadStart?.();
    if (finishReload) {
      loading.then(finishReload, finishReload);
    }
    return loading;
  };

  const loadDataThrottled = throttle(loadData, 200);

  return {
    loadData,
    loadDataThrottled,
    touchLastLoaded,
    handleError,
  };
}
